import { useState, useEffect } from 'react';
import axios from 'axios';
import { Lightbulb, Check } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import BookingZone from '@/components/BookingZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConseilPage = () => {
  const [pageSettings, setPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageRes = await axios.get(`${API}/pages/conseil`);
        setPageSettings(pageRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const services = [
    "Diagnostic et étude de site",
    "Étude paysagère et schémas de plantation",
    "Plan 2D/3D de jardin",
    "Conseil en choix de végétaux",
    "Projet de jardin thématique",
    "Accompagnement personnalisé"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-12 lg:py-20 pt-28 lg:pt-36" data-testid="conseil-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-blue-100 text-blue-700 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Lightbulb className="w-4 h-4 inline mr-2" />
              Conseils & Études
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900 mb-4">
              {pageSettings?.title || "Conseils & Études paysagères"}
            </h1>
            <p className="text-stone-600 text-lg">
              {pageSettings?.description || "Bénéficiez de l'expertise de nos paysagistes pour concevoir le jardin de vos rêves."}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 lg:py-16" data-testid="conseil-content">
        <div className="container-custom">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Booking Zone - First on mobile */}
            <div className="lg:order-2 bg-white rounded-3xl shadow-xl border border-stone-100 p-4 sm:p-6 lg:p-8">
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-4 sm:mb-6 text-center">
                Prendre rendez-vous
              </h2>
              <div className="iframe-container">
                <BookingZone 
                  embedHtml={pageSettings?.embed_html} 
                  placeholder="Le formulaire de prise de RDV sera configuré dans le back-office"
                />
              </div>
            </div>

            {/* Services - Second on mobile */}
            <div className="lg:order-1">
              <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">
                Nos services de conseil
              </h2>
              
              <div className="bg-blue-50 rounded-2xl p-6 mb-8">
                <p className="text-blue-800 font-semibold mb-2">Expertise professionnelle</p>
                <p className="text-blue-600 font-bold text-xl">Étude sur mesure</p>
                <p className="text-stone-600 text-sm mt-2">
                  Nos paysagistes vous accompagnent dans la conception de votre projet
                </p>
              </div>

              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-stone-700">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-blue-600" data-testid="conseil-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-2xl text-white mb-4">
            Besoin de conseils ?
          </h2>
          <p className="text-blue-100 mb-6">
            Prenez rendez-vous avec un de nos experts paysagistes.
          </p>
          <a 
            href="tel:0465011022" 
            className="inline-flex items-center gap-2 bg-white text-blue-600 font-semibold px-8 py-4 rounded-full hover:bg-stone-100 transition-colors text-lg"
          >
            04 65 01 10 22
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ConseilPage;
